/**
 * UI Utility Functions
 *
 * Purpose: Centralized UI utility functions for the OrchestrateHQ AI frontend
 *          including responsive design, accessibility, and user interaction helpers.
 *
 * Architecture: Part of the frontend utilities module, used across all components
 *              for consistent UI behavior and accessibility.
 *
 * Dependencies: React hooks, Material-UI
 * Author: Development Team
 * Created: 2025-01-27
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

/**
 * Custom hook for responsive breakpoints
 *
 * Purpose: Provides easy access to responsive breakpoints for component styling
 * Logic: Uses Material-UI's useMediaQuery with theme breakpoints
 *
 * @returns Object with boolean values for each breakpoint
 */
export const useResponsive = () => {
  const theme = useTheme();

  return {
    isMobile: useMediaQuery(theme.breakpoints.down('sm')),
    isTablet: useMediaQuery(theme.breakpoints.between('sm', 'md')),
    isDesktop: useMediaQuery(theme.breakpoints.up('md')),
    isLargeScreen: useMediaQuery(theme.breakpoints.up('lg')),
    isExtraLarge: useMediaQuery(theme.breakpoints.up('xl')),
  };
};

/**
 * Custom hook for local storage with error handling
 *
 * Purpose: Provides safe local storage access with fallback handling
 * Logic: Wraps localStorage with try-catch and provides default values
 *
 * @param key - Storage key
 * @param defaultValue - Default value if key doesn't exist
 * @returns Tuple with current value and setter function
 */
export const useLocalStorage = <T>(key: string, defaultValue: T): [T, (value: T) => void] => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  const setValue = useCallback(
    (value: T) => {
      try {
        setStoredValue(value);
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key]
  );

  return [storedValue, setValue];
};

/**
 * Custom hook for debounced function calls
 *
 * Purpose: Prevents excessive function calls during rapid user input
 * Logic: Delays function execution until user stops typing
 *
 * @param callback - Function to debounce
 * @param delay - Delay in milliseconds (default: 300)
 * @returns Debounced function
 */
export const useDebounce = <T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 300
): T => {
  const timeoutRef = useRef<NodeJS.Timeout>();

  return useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  ) as T;
};

/**
 * Custom hook for click outside detection
 *
 * Purpose: Detects clicks outside a component for closing modals/dropdowns
 * Logic: Uses event listeners to detect clicks outside the ref element
 *
 * @param callback - Function to call when click outside is detected
 * @returns Ref to attach to the element
 */
export const useClickOutside = (callback: () => void) => {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [callback]);

  return ref;
};

/**
 * Custom hook for keyboard shortcuts
 *
 * Purpose: Handles keyboard shortcuts for better user experience
 * Logic: Uses event listeners to detect key combinations
 *
 * @param shortcuts - Object mapping key combinations to callback functions
 */
export const useKeyboardShortcuts = (shortcuts: Record<string, () => void>) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const ctrl = event.ctrlKey || event.metaKey;
      const shift = event.shiftKey;
      const alt = event.altKey;

      // Build shortcut string
      const parts = [];
      if (ctrl) parts.push('ctrl');
      if (shift) parts.push('shift');
      if (alt) parts.push('alt');
      parts.push(key);

      const shortcut = parts.join('+');

      if (shortcuts[shortcut]) {
        event.preventDefault();
        shortcuts[shortcut]();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [shortcuts]);
};

/**
 * Custom hook for scroll position tracking
 *
 * Purpose: Tracks scroll position for scroll-based animations and effects
 * Logic: Uses scroll event listener to update position state
 *
 * @returns Current scroll position
 */
export const useScrollPosition = () => {
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.pageYOffset);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return scrollPosition;
};

/**
 * Custom hook for window size tracking
 *
 * Purpose: Tracks window dimensions for responsive behavior
 * Logic: Uses resize event listener to update dimensions
 *
 * @returns Object with window width and height
 */
export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return windowSize;
};

/**
 * Custom hook for focus management
 *
 * Purpose: Manages focus for accessibility and keyboard navigation
 * Logic: Provides functions to trap focus within a container
 *
 * @param containerRef - Ref to the container element
 * @returns Object with focus management functions
 */
export const useFocusManagement = (containerRef: React.RefObject<HTMLElement>) => {
  const trapFocus = useCallback(() => {
    if (!containerRef.current) return;

    const focusableElements = containerRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => {
      document.removeEventListener('keydown', handleTabKey);
    };
  }, [containerRef]);

  return { trapFocus };
};

/**
 * Custom hook for loading states
 *
 * Purpose: Manages loading states with timeout and error handling
 * Logic: Provides loading state with automatic timeout and error handling
 *
 * @param timeout - Timeout in milliseconds (default: 10000)
 * @returns Object with loading state and control functions
 */
export const useLoadingState = (timeout: number = 10000) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const startLoading = useCallback(() => {
    setIsLoading(true);
    setError(null);

    // Set timeout to prevent infinite loading
    timeoutRef.current = setTimeout(() => {
      setIsLoading(false);
      setError('Request timed out');
    }, timeout);
  }, [timeout]);

  const stopLoading = useCallback(() => {
    setIsLoading(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const setLoadingError = useCallback((errorMessage: string) => {
    setIsLoading(false);
    setError(errorMessage);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isLoading,
    error,
    startLoading,
    stopLoading,
    setLoadingError,
  };
};

/**
 * Custom hook for form validation
 *
 * Purpose: Manages form validation state and error messages
 * Logic: Tracks field errors and provides validation helpers
 *
 * @param initialValues - Initial form values
 * @returns Object with validation state and helper functions
 */
export const useFormValidation = <T extends Record<string, any>>(_initialValues: T) => {
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});

  const setFieldError = useCallback((field: keyof T, error: string) => {
    setErrors((prev) => ({ ...prev, [field]: error }));
  }, []);

  const clearFieldError = useCallback((field: keyof T) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  const setFieldTouched = useCallback((field: keyof T) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const validateField = useCallback(
    (field: keyof T, value: any, validator?: (value: any) => string | null) => {
      if (validator) {
        const error = validator(value);
        if (error) {
          setFieldError(field, error);
        } else {
          clearFieldError(field);
        }
      }
    },
    [setFieldError, clearFieldError]
  );

  const isValid = Object.keys(errors).length === 0;

  return {
    errors,
    touched,
    isValid,
    setFieldError,
    clearFieldError,
    setFieldTouched,
    validateField,
  };
};
